/**
 * DigiLockerComplete — Landing page after DigiLocker callback.
 * Polls /api/digilocker/result/:state and displays the fetched KYC bundle.
 */
import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Shield, CheckCircle2, AlertTriangle, Loader2, FileText,
  User, Calendar, MapPin, IdCard, Sparkles, ArrowRight,
} from "lucide-react";

const API = `${window.location.origin}/api/digilocker`;

export default function DigiLockerComplete() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const state = params.get("state");
  const error = params.get("error");
  const [bundle, setBundle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  const fetchBundle = useCallback(async () => {
    if (!state) { setLoading(false); return; }
    try {
      const { data } = await axios.get(`${API}/result/${state}`);
      setBundle(data);
    } catch (e) {
      setFetchError(e.response?.data?.detail || "Could not load DigiLocker result");
    } finally {
      setLoading(false);
    }
  }, [state]);

  useEffect(() => { if (!error) fetchBundle(); else setLoading(false); }, [fetchBundle, error]);

  return (
    <div data-testid="digilocker-complete-page" style={{ minHeight: "100vh", background: "#080c12", padding: "40px 20px", fontFamily: "system-ui, -apple-system, sans-serif", color: "#f1f5f9" }}>
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ marginBottom: 28, textAlign: "center" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 10, background: "#0d1117", border: "1px solid #1e2530", padding: "8px 16px", borderRadius: 999, marginBottom: 14 }}>
            <Shield size={16} style={{ color: "#3b82f6" }} />
            <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: 0.3 }}>DigiLocker · Rudrik</span>
          </div>
          <h1 style={{ fontSize: 26, fontWeight: 700, margin: 0, marginBottom: 6 }}>
            {error ? "Verification Cancelled" : "Identity Verified"}
          </h1>
          <p style={{ color: "#94a3b8", fontSize: 13, margin: 0 }}>
            {error ? "You can retry whenever you're ready." : "Your documents have been securely fetched from DigiLocker."}
          </p>
        </div>

        {loading && (
          <Card>
            <div style={{ padding: 60, display: "flex", flexDirection: "column", alignItems: "center", gap: 14 }}>
              <Loader2 className="animate-spin" size={28} style={{ color: "#3b82f6" }} />
              <div style={{ color: "#94a3b8", fontSize: 13 }}>Fetching verified documents from DigiLocker...</div>
            </div>
          </Card>
        )}

        {error && (
          <Card>
            <div style={{ padding: 28, display: "flex", flexDirection: "column", alignItems: "center", gap: 12, textAlign: "center" }}>
              <AlertTriangle size={32} style={{ color: "#f59e0b" }} />
              <div style={{ fontSize: 15, fontWeight: 600 }}>{error.replace(/_/g, " ")}</div>
              <button onClick={() => navigate(-1)} style={btnStyle}>Try Again</button>
            </div>
          </Card>
        )}

        {fetchError && !loading && (
          <Card>
            <div style={{ padding: 28, color: "#ef4444", textAlign: "center", fontSize: 13 }}>
              <AlertTriangle size={28} style={{ marginBottom: 10 }} />
              <div>{fetchError}</div>
            </div>
          </Card>
        )}

        {bundle && !loading && (
          <>
            {bundle.is_mock && (
              <div style={{ background: "rgba(245,158,11,0.1)", border: "1px solid #f59e0b55", borderRadius: 10, padding: 12, marginBottom: 16, display: "flex", gap: 10, alignItems: "flex-start" }}>
                <Sparkles size={14} style={{ color: "#f59e0b", marginTop: 2, flexShrink: 0 }} />
                <div style={{ color: "#fcd34d", fontSize: 11, lineHeight: 1.5 }}>
                  <strong>Sandbox Demo Data</strong> — yeh demo profile hai. Real production credentials (`.env`) configure karne pe actual DigiLocker se data aayega.
                </div>
              </div>
            )}

            {/* Profile card */}
            {bundle.profile && (
              <Card>
                <div style={{ padding: 20 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
                    <div style={{ background: "rgba(34,197,94,0.15)", width: 36, height: 36, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <CheckCircle2 size={18} style={{ color: "#22c55e" }} />
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase" }}>Aadhaar eKYC</div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: "#f1f5f9" }}>Identity Verified</div>
                    </div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14, padding: 14, background: "#080c12", borderRadius: 8 }}>
                    <Field icon={User} label="Name" value={bundle.profile.name} />
                    <Field icon={Calendar} label="Date of Birth" value={bundle.profile.dob} />
                    <Field icon={IdCard} label="Gender" value={bundle.profile.gender} />
                    {bundle.profile.aadhaar_last4 && <Field icon={Shield} label="Aadhaar (last 4)" value={`••••${bundle.profile.aadhaar_last4}`} mono />}
                    {bundle.profile.pan && <Field icon={IdCard} label="PAN" value={bundle.profile.pan} mono />}
                    {bundle.profile.address && <Field icon={MapPin} label="Address" value={bundle.profile.address} wide />}
                  </div>
                </div>
              </Card>
            )}

            {/* Documents list */}
            {bundle.documents?.length > 0 && (
              <Card style={{ marginTop: 14 }}>
                <div style={{ padding: 20 }}>
                  <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, letterSpacing: 0.6, textTransform: "uppercase", marginBottom: 12 }}>
                    Issued Documents ({bundle.documents.length})
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {bundle.documents.map((d) => (
                      <div key={d.uri} data-testid={`dl-doc-${d.doctype}`} style={{ padding: 12, background: "#080c12", borderRadius: 6, border: "1px solid #1e2530", display: "flex", alignItems: "center", gap: 12 }}>
                        <FileText size={16} style={{ color: "#3b82f6", flexShrink: 0 }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 13, fontWeight: 600 }}>{d.description || d.name}</div>
                          <div style={{ fontSize: 10, color: "#64748b" }}>{d.issuer} · Issued {d.date}</div>
                        </div>
                        <span style={{ background: "rgba(34,197,94,0.12)", color: "#22c55e", border: "1px solid #22c55e55", fontSize: 9, padding: "3px 8px", borderRadius: 3, fontWeight: 700, letterSpacing: 0.4, textTransform: "uppercase" }}>Verified</span>
                      </div>
                    ))}
                  </div>
                </div>
              </Card>
            )}

            <div style={{ marginTop: 22, textAlign: "center" }}>
              <button
                data-testid="dl-continue-btn"
                onClick={() => window.close() || navigate("/")}
                style={{ ...btnStyle, padding: "12px 28px" }}
              >
                Continue <ArrowRight size={14} />
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Card({ children, style }) {
  return (
    <div style={{ background: "#0d1117", border: "1px solid #1e2530", borderRadius: 12, overflow: "hidden", ...style }}>
      {children}
    </div>
  );
}

function Field({ icon: Icon, label, value, mono, wide }) {
  return (
    <div style={{ gridColumn: wide ? "1 / -1" : undefined }}>
      <div style={{ color: "#64748b", fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 4, display: "flex", alignItems: "center", gap: 5 }}>
        <Icon size={10} /> {label}
      </div>
      <div style={{ color: "#f1f5f9", fontSize: 13, fontWeight: 600, fontFamily: mono ? "ui-monospace, monospace" : "inherit" }}>
        {value || "—"}
      </div>
    </div>
  );
}

const btnStyle = {
  background: "linear-gradient(135deg, #2563eb, #1d4ed8)", color: "#fff", border: "none",
  padding: "10px 22px", borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer",
  display: "inline-flex", alignItems: "center", gap: 8,
};
